import { TagFilled } from '@ant-design/icons';
import { Button, Card, Col, Form, Row, Space, Tooltip } from 'antd';
import EditableNorm from 'app/components/EditableNorm/EditableNorm';
import NumberInput from 'app/components/NumberInput/NumberInput';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import { PermissionsEnum } from 'app/containers/User/slice/types';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { groupBy, toFixedIfFloat } from 'utils/helpers';
import { checkIsPositiveNumber } from 'utils/validationHelpers';
import CultureIcon from '../../icons/CultureIcon';
import { useResourceSlice } from '../../slice';
import {
  selectResourceDistributionState,
  selectResourcesAllocationChangesLoading,
  selectSelectedTaskCategoryId,
  selectSelectedUsedResource,
  selectSelectedUsedResourceId,
} from '../../slice/selectors';
import {
  IResourceDistributionListItem,
  IWarehouseFilters,
} from '../../slice/types';

import * as S from './TaskUsedResourceDistributionCard.styles';

interface IProps {
  filters: IWarehouseFilters;
}

const TaskUsedResourceDistributionCard: FC<IProps> = ({ filters }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { actions } = useResourceSlice();

  const [normForm] = Form.useForm();

  const [amountForm] = Form.useForm();

  const submitLoading = useSelector(selectResourcesAllocationChangesLoading);
  const usedResource = useSelector(selectSelectedUsedResource);
  const usedResourceId = useSelector(selectSelectedUsedResourceId);
  const companyId = useSelector(selectSelectedCompanyId);
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);
  const envId = useSelector(selectSelectedEnvId);
  const seasonId = useSelector(selectSelectedSeasonId);

  const { resourcesArr: resourceDistributionArr, loading } = useSelector(
    selectResourceDistributionState,
  );
  const selectedCategoryId = useSelector(selectSelectedTaskCategoryId);

  const [selectedRowKeysArr, setSelectedRowKeysArr] = useState<
    (string | number)[][]
  >([]);

  const [updatedAmountArr, setUpdatedAmountArr] = useState<
    { id: number; amount: number; fieldTaskId: number; resourceId: number }[]
  >([]);

  useEffect(() => {
    if (usedResourceId && envId && seasonId && filters && selectedCategoryId) {
      dispatch(
        actions.getUsedResourceDistribution({
          seasonId,
          envId,
          resourceId: usedResourceId,
          companyId,
          companyGroupId,
          filters,
          taskCategoryId: selectedCategoryId,
        }),
      );
      setSelectedRowKeysArr([]);
      setUpdatedAmountArr([]);
    }
  }, [
    seasonId,
    usedResourceId,
    envId,
    actions,
    dispatch,
    filters,
    companyId,
    companyGroupId,
    selectedCategoryId,
  ]);

  const updateAmountValue = useCallback(
    (item: IResourceDistributionListItem, amount: number) => {
      setUpdatedAmountArr(prevUpdatedAmounts => {
        const itemIdx = prevUpdatedAmounts.findIndex(
          prevItem => prevItem.id === item.id,
        );

        if (itemIdx === -1) {
          return [
            ...prevUpdatedAmounts,
            {
              id: item.id,
              amount,
              fieldTaskId: item.fieldTask.id,
              resourceId: item.resource.id,
            },
          ];
        } else {
          prevUpdatedAmounts[itemIdx].amount = amount;
          return [...prevUpdatedAmounts];
        }
      });
    },
    [setUpdatedAmountArr],
  );

  const [activeNormInputResourceId, setActiveNormInputItemId] = useState<
    number | null
  >(null);

  const renderUsedArea = useCallback(
    (_: unknown, item: IResourceDistributionListItem) => {
      const totalArea = toFixedIfFloat(item.fieldSeason.area);
      const usedArea = toFixedIfFloat(
        (item.fieldTask.progress / 100) * totalArea,
      );
      if (totalArea !== usedArea) {
        return (
          <Tooltip
            title={t(
              'Resource.UserResourceDistribution.UsedAreaTotalArea',
              'Used area / Total area',
            )}
          >{`${usedArea} / ${totalArea}`}</Tooltip>
        );
      } else return usedArea;
    },
    [t],
  );

  const columns = useMemo(
    () => [
      {
        title: t('Resource.UserResourceDistribution.Field', 'Field'),
        dataIndex: ['fieldSeason', 'field'],
        key: 'fieldName',
        render: (fieldName?: string) => fieldName || '-',
        sorter: {
          compare: (
            a: IResourceDistributionListItem,
            b: IResourceDistributionListItem,
          ) => {
            return a.fieldSeason.field.localeCompare(b.fieldSeason.field);
          },
        },
      },
      {
        title: t('Resource.UserResourceDistribution.Ha', 'Ha'),
        dataIndex: ['fieldSeason', 'area'],
        key: 'area',
        width: '21%',

        render: renderUsedArea,
        sorter: {
          compare: (
            a: IResourceDistributionListItem,
            b: IResourceDistributionListItem,
          ) => {
            return a.fieldSeason.area - b.fieldSeason.area;
          },
        },
      },
      {
        title: t('Resource.UserResourceDistribution.Norm', {
          defaultValue: 'Norm ({{unit}}/Ha)',
          unit: usedResource?.resource.type.unit,
        }),
        dataIndex: 'norm',
        key: 'norm',
        width: '21%',

        render: (_: unknown, item: IResourceDistributionListItem) => (
          <EditableNorm
            activeNormInputResourceId={activeNormInputResourceId}
            norm={Number(
              toFixedIfFloat(
                item.amount /
                  ((item.fieldTask.progress / 100) * item.fieldSeason.area),
              ),
            )}
            recommendedNorm={item.resource.recommendedNorm}
            showWarningCircle={true}
            unit={item.resource.type.unit}
            resourceId={item.id}
            onChange={(updatedNorm: number) => {
              updateAmountValue(
                item,
                (item.fieldTask.progress / 100) *
                  item.fieldSeason.area *
                  updatedNorm,
              );
            }}
            onOpen={() => setActiveNormInputItemId(item.id)}
            onClose={() => setActiveNormInputItemId(null)}
            isEditable={true}
          />
        ),
        sorter: {
          compare: (
            a: IResourceDistributionListItem,
            b: IResourceDistributionListItem,
          ) => {
            const aNorm =
              a.amount / ((a.fieldTask.progress / 100) * a.fieldSeason.area);
            const bNorm =
              b.amount / ((b.fieldTask.progress / 100) * b.fieldSeason.area);

            return aNorm - bNorm;
          },
        },
      },
      {
        title: t('Resource.UserResourceDistribution.Used', {
          defaultValue: 'Used ({{unit}})',
          unit: usedResource?.resource.type.unit,
        }),
        dataIndex: 'amount',
        key: 'amount',
        width: '21%',

        render: (amount: number) => toFixedIfFloat(amount),
        sorter: {
          compare: (
            a: IResourceDistributionListItem,
            b: IResourceDistributionListItem,
          ) => {
            return a.amount - b.amount;
          },
        },
      },
    ],
    [
      t,
      usedResource,
      activeNormInputResourceId,
      updateAmountValue,
      renderUsedArea,
    ],
  );

  const resourceDistributionArrWithUpdates = resourceDistributionArr.map(
    res => {
      const updated = updatedAmountArr.find(
        updatedResource => updatedResource.id === res.id,
      );
      if (updated) {
        return { ...res, amount: updated.amount };
      }
      return res;
    },
  );

  const groupedTablesByCulture = useMemo(
    () =>
      Array.from(
        groupBy(
          resourceDistributionArrWithUpdates,
          (item: IResourceDistributionListItem) => item.culture?.id,
        ),
      ).map((arr: [unknown, IResourceDistributionListItem[]]) => ({
        arr: arr[1],
        culture: arr[1][0].culture,
      })),
    [resourceDistributionArrWithUpdates],
  );

  const onNormInputSubmit = ({
    updatedNorm,
  }: {
    updatedNorm: number | string;
  }) => {
    resourceDistributionArrWithUpdates
      .filter(
        item =>
          selectedRowKeysArr.flat(1).findIndex(id => item.id === id) !== -1,
      )
      .map(item =>
        updateAmountValue(
          item,

          (item.fieldTask.progress / 100) *
            item.fieldSeason.area *
            Number(updatedNorm),
        ),
      );
    normForm.resetFields();
  };

  const onAmountInputSubmit = ({
    additionalAmount,
  }: {
    additionalAmount: number | string;
  }) => {
    let totalArea = 0;
    const additionalAmountNum = Number(additionalAmount);

    const fieldsToUpdate = resourceDistributionArrWithUpdates
      .filter(
        item =>
          selectedRowKeysArr.flat(1).findIndex(id => item.id === id) !== -1,
      )
      .map(item => {
        const usedArea =
          (item.fieldTask.progress / 100) * item.fieldSeason.area;
        totalArea += usedArea;

        return {
          item,
          area: usedArea,
          amount: item.amount,
        };
      });

    fieldsToUpdate.forEach(item => {
      const updatedAmount =
        item.amount + (additionalAmountNum * item.area) / totalArea;
      updateAmountValue(item.item, updatedAmount);
    });
    amountForm.resetFields();
  };

  const onSave = () => {
    const updatesArr = updatedAmountArr.map(({ id, ...withoutId }) => {
      return withoutId;
    });

    if (envId && updatesArr.length) {
      dispatch(
        actions.submitUsedResourcesUpdates({
          envId,
          usedResourcesArr: updatesArr,
          filters,
        }),
      );
    }
  };

  if (!usedResource) {
    return null;
  }

  return (
    <Card
      title={usedResource.resource.name}
      extra={[
        <PermissionsWrapper
          key="1"
          requiredEditPermissions={[PermissionsEnum.resourceAdministration]}
        >
          <Button type="primary" onClick={onSave} loading={submitLoading}>
            {t('Resource.UserResourceDistribution.Save', 'Save')}
          </Button>
        </PermissionsWrapper>,
      ]}
      style={{
        maxHeight: 'calc(100vh - 165px)',
        overflow: 'auto',
      }}
    >
      <Row style={{ marginBottom: 20 }}>
        <TagFilled
          style={{ color: '#FFA39E', fontSize: 19, marginRight: 12 }}
        />
        {t('Resource.UserResourceDistribution.RecommendedDensity', {
          defaultValue: 'Recommended density: {{recommendedNorm}} {{unit}}/Ha',
          recommendedNorm: usedResource.resource.recommendedNorm,
          unit: usedResource.resource.type.unit,
        })}{' '}
      </Row>
      <PermissionsWrapper
        key="0"
        requiredEditPermissions={[PermissionsEnum.resourceAdministration]}
      >
        <Row style={{ marginBottom: 20 }}>
          <Col flex={1}>
            <Form
              layout={'vertical'}
              form={normForm}
              initialValues={{ layout: 'vertical' }}
              id={'changeNormInFields'}
              onFinish={onNormInputSubmit}
              requiredMark={'optional'}
            >
              <Row>
                <Space>
                  <Form.Item
                    name={'updatedNorm'}
                    label={t(
                      'Resource.UserResourceDistribution.ChangeFieldsNorm',
                      'Change fields norm',
                    )}
                    rules={[
                      { required: true },
                      { validator: checkIsPositiveNumber },
                    ]}
                  >
                    <NumberInput
                      placeholder={t('Resource.UserResourceDistribution.Norm', {
                        defaultValue: 'Norm',
                        unit: usedResource.resource.type.unit,
                      })}
                    />
                  </Form.Item>
                  <Button
                    type="primary"
                    key="submit"
                    htmlType="submit"
                    form={'changeNormInFields'}
                    disabled={!selectedRowKeysArr.flat(1).length}
                  >
                    {t('Resource.UserResourceDistribution.Change', 'Change')}
                  </Button>
                </Space>
              </Row>
            </Form>
          </Col>
          <Col flex={1}>
            <Form
              layout={'vertical'}
              form={amountForm}
              initialValues={{ layout: 'vertical' }}
              id={'addAmountToFields'}
              onFinish={onAmountInputSubmit}
              requiredMark={'optional'}
            >
              <Row>
                <Space>
                  <Form.Item
                    name={'additionalAmount'}
                    label={t(
                      'Resource.UserResourceDistribution.DivideAmountInFields',
                      'Divide amount in fields',
                    )}
                    rules={[
                      { required: true },
                      { validator: checkIsPositiveNumber },
                    ]}
                  >
                    <NumberInput
                      placeholder={t(
                        'Resource.UserResourceDistribution.Amount',
                        'Amount',
                      )}
                    />
                  </Form.Item>
                  <Button
                    type="primary"
                    key="submit"
                    htmlType="submit"
                    form={'addAmountToFields'}
                    disabled={!selectedRowKeysArr.flat(1).length}
                  >
                    {t('Resource.UserResourceDistribution.Divide', 'Divide')}
                  </Button>
                </Space>
              </Row>
            </Form>
          </Col>
        </Row>
      </PermissionsWrapper>

      {groupedTablesByCulture.map(({ culture, arr }, groupIndex) => (
        <Col style={{ marginBottom: 30 }} key={culture?.id}>
          <Space size="middle" style={{ marginBottom: 10 }}>
            <S.CultureCircle $color={culture?.color}>
              <CultureIcon />
            </S.CultureCircle>
            <span>
              {culture?.name || t('Common.WithoutCulture', 'Without culture')}
            </span>
          </Space>
          <StyledTable
            rowSelection={{
              selectedRowKeys: selectedRowKeysArr[groupIndex],
              onChange: selectedKeys => {
                setSelectedRowKeysArr(prevArrState => {
                  const updatedArr = [...prevArrState];
                  updatedArr[groupIndex] = selectedKeys;
                  return updatedArr;
                });
              },
            }}
            rowKey={item => item.id}
            loading={loading}
            bordered={true}
            columns={columns}
            dataSource={arr}
            pagination={false}
          />
        </Col>
      ))}
    </Card>
  );
};

export default TaskUsedResourceDistributionCard;
